<template>
    <div :id="containerId" :class="containterClassObject">
        <div v-if="useDefaultAlerts" class="editable__alerts">
            <div v-for="(alert, index) in alerts" :class="'editable__alert editable__alert--' + alert.type" :key="index">
                {{ alert.text }}
            </div>
        </div>

        <slot :state="state" 
            :subState="subState"
            :setState="setState" 
            :toggleState="toggleState" 
            :getData="getData"
            :set="set"
            :setData="setData"
            :setAdditionalDataKey="setAdditionalDataKey"
            :validate="validate"
            :data="childrenData"
            :additionalData="dataAdditional"
            :children="childrenInstances"
            :removeEmptyChildren="removeEmptyChildren"
            :hasUnsavedData="hasUnsavedData"
            :save="save" 
            :showStatusExpired="showStatusExpired" 
            :redirectPage="redirectPage" 
            :alerts="alerts" />

        <button v-if="showToggleButton" class="editable__btn-toggle-state" @click="toggleState"></button>
        
        <div :class="'editable__state-layers editable__state-layers--' + subState">
            <div v-if="subState == 'loading'" class="editable__state-layer editable__state-layer--loading">
                <spinner></spinner>
            </div>
            <div v-if="subState == 'success'" class="editable__state-layer editable__state-layer--success">
                <img src="../../../img/icons/check-circle-green.svg" width="50" height="50" />
            </div>
        </div>
    </div>
</template>

<script>
import { getCsrfToken, generateFormData } from '../../helpers/forms';
import Base from './Base';
import HasState from './Editable/Mixins/HasState';
import HasData from './Editable/Mixins/HasData';
import HasEditableChildren from './Editable/Mixins/HasEditableChildren';
import HasEditableParent from './Editable/Mixins/HasEditableParent';
import { isPlainObject, unflattenObject, cloneObject, isFetchResponse } from '../../helpers/common';

export default {
    extends: Base,
    mixins: [ HasState, HasData, HasEditableParent, HasEditableChildren ],
    props: {
        dataUrl: {
            type: String
        },
        additionalData: {
            type: Object
        },
        showToggleButton: {
            type: Boolean,
            default: true
        },
        containerClass: {
            type: String
        },
        containerId: {
            type: String
        },
        noValueText: {
            type: String,
            default: ''
        },
        noValueClass: {
            type: String
        },
        saveUrl: {
            type: String
        },
        saveProcessor: {
            type: Function
        },
        successMessage: {
            type: String
        },
        useDefaultAlerts: {
            type: Boolean,
            default: true
        },
        triggerChangedAfterSave: {
            type: Boolean,
            default: false
        },
        bubbleChangedByUserInput: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            data: {},
            dataAdditional: this.additionalData,
            saveError: null,
            childEditables: []
        }
    },
    computed: {
        noValText() {
            return this.noValueText;
        },
        noValClass() {
            return this.noValueClass;
        },
        containterClassObject() {
            let classObject = { 'editable': true };
            classObject['editable--' + this.state] = true;
            classObject['editable--' + this.subState] = this.subState ? true : false;
            classObject[this.containerClass] = this.containerClass ? true : false;

            return classObject;
        },
        hasUnsavedData() {
            return this.changedByUserInput || this.childEditables.some(e => e.changedByUserInput);
        }
    },
    created() {
        this._isEditable = true;

        if(this.dataUrl) {
            let editableParent = this.getEditableParent();

            if(!editableParent || !this.baseName || !(this.baseName in editableParent.childrenData)) {
                this.loadData(this.dataUrl);
            }
        }
    },
    methods: {
        onChildReady(child) {
            this.childReady(child);

            if(child._isEditable) {
                this.childEditables.push(child);
            }
        },

        onChildDestroyed(child) {
            if(child._isEditable) {
                const childEditableIndex = this.childEditables.findIndex(c => c._uid == child._uid);

                if(childEditableIndex != -1) {
                    this.childEditables.splice(childEditableIndex, 1);
                }
            }

            this.childDestroyed(child);
        },

        loadData(url) {
            let preLoadingSubState = this.subState;
            this.subState = 'loading';

            fetch(url).then(resp => {
                return resp.json();
            }).then(json => {
                if(('success' in json) && json.success && ('data' in json)) {
                    json = json.data;
                }

                this.setData(json);
                this.dataChanged();
                this.subState = preLoadingSubState;
            });
        },

        toggleState() {
            if(this.state == 'edit') {
                this.save().catch(err => {
                    this.saveError = err;
                });
            } else {
                this.setState(this.state == 'show' ? 'edit' : 'show');
            }
        },

        setState(newState, subState = null, subStateTempDuration = 0) {
            //this.getEditableChildren().forEach(child => child.state = newState);

            if(subStateTempDuration) {
                window.setTimeout((finalState) => {
                    this.state = finalState;
                    this.subState = null;
                }, subStateTempDuration, newState);

                this.subState = subState;
            } else {
                this.state = newState;
                this.subState = subState;
            }
        },

        save(url = null, onSuccess = null, onError = null, redirect = false) {
            return new Promise((resolve, reject) => {
                if(url && typeof url == 'function') {
                    onError = onSuccess;
                    onSuccess = url;
                    url = null;
                }

                this.setErrors(null);
                this.saveError = null;
                this.subState = 'loading';

                this.processSave(url).then((response = {}) => {
                    this.setState(this.defaultState, 'success', 1500);

                    if(response.changed && this.successMessage) {
                        this.alerts = [ { type: 'success', text: this.successMessage } ];
                    }
                    if('alerts' in response) {
                        this.alerts = response.alerts;
                    }

                    let setResponseData = 'data' in response;
                    if(onSuccess) {
                        if(redirect ){
                            if(response.data.jobID){
                            
                            window.location = '/jobs/servicing/' + response.data.jobID  +'/edit'
                            }else{
                                let errors = 'Bitte überprüfen Sie die Felder';
                                this.setState('edit');
                                this.setErrors(errors);
                                reject(errors);
                                return;
                            }
                        }
                        setResponseData = onSuccess(this, cloneObject(response));
                    }

                    this.$emit('success', this, response);

                    if(setResponseData !== false && response.data) {
                        this.setData(response.data);

                        if(this.triggerChangedAfterSave) {
                            this.dataChanged();
                        }
                    }

                    this.changedByUserInput = false;
                    this.childEditables.forEach(editable => editable.changedByUserInput = false);

                    resolve();
                }, errors => {
                    errors = this.parseSaveErrors(errors);

                    this.setState('edit');
                    this.setErrors(errors);

                    if(onError) {
                        onError(this, errors);
                    }
                    reject(errors);
                });
            });
        },
        
        processSave(url) {
            if(!url) {
                url = this.saveUrl;
            }

            if(this.saveProcessor) {
                return this.handleSavePromise(this.saveProcessor(url, this.getData(), this));
            } else {
                if(url) {
                    let formData = generateFormData(this.getData());

                    return this.handleSavePromise(fetch(url, {
                        method: 'POST',
                        body: formData,
                        headers: {
                            'X-CSRF-TOKEN': getCsrfToken(),
                            'Accept': 'application/json'
                        }
                    }));
                } else {
                    return Promise.reject('No save url or processor defined');
                }
            }
        },

        handleSavePromise(savePromise) {
            return new Promise((resolve, reject) => {
                let response = null;

                savePromise.then(resp => {
                    response = resp;

                    if(isFetchResponse(response)) {
                        if(response.status == 200 || response.status == 422) {
                            return resp.json();
                        } else {
                            let rejectMessage = __('main.common.response_codes.' + response.status) || 'Invalid response (' + response.status + ': ' + response.statusText + ')';
                            return Promise.reject(rejectMessage);
                        }
                    } else {
                        return resp;
                    }
                }).then(result => {
                    if(isFetchResponse(response)) {
                        if(response.status == 422) {
                            reject(result.errors);
                        } else {
                            if(result.success) {
                                resolve({
                                    changed: (!('changed' in result) || result.changed),
                                    data: result.data || null
                                });
                            } else {
                                reject('Invalid response data');
                            }
                        }
                    } else {
                        resolve(result);
                    }
                }).catch(error => {
                    reject(error);
                });
            });
        },

        setAdditionalData(newData) {
            let data = newData;

            if(this.dataAdditional && isPlainObject(newData)) {
                data = {};

                Object.keys(newData).forEach(key => {
                    if(!(key in this.childrenInstances)) {
                        this.dataAdditional[key] = newData[key];
                    } else {
                        data[key] = newData[key];
                    }
                });
            }

            return data;
        },

        parseSaveErrors(errors) {
            if(errors && isPlainObject(errors)) {
                return unflattenObject(errors);
            }

            return errors;
        },

        bubbleUpChangedByUserInput(newValue) {
            const shouldBubbleChangedByUserInput = this.bubbleChangedByUserInput || !(this.saveUrl || this.saveProcessor);

            if(this.parentEditable && shouldBubbleChangedByUserInput) {
                this.parentEditable.changedByUserInput = newValue;
            }
        },

        onChanged() {
            this.$emit('change', { data: arguments.length > 0 ? arguments[0] : this.childrenData, source: this });
            this.$emit('datachange', { data: arguments.length > 0 ? arguments[0] : this.childrenData, source: this });
        },

        redirectPage(valueId) {
            window.location = valueId 
        },
        showStatusExpired(data){
            const date = new Date(data.created_at);
            const currentDate = new Date();

            const numOfHours = data.acceptance_period;
            date.setTime(date.getTime() + numOfHours * 60 * 60 * 1000);

            return date < currentDate
        }
    }
}
</script>

<style lang="scss">
.editable {
    position: relative;

    p {
        margin: 0;
    }

    &__btn-toggle-state {
        position: absolute;
        right: 10px;
        top: 10px;
    }

    &--edit &__btn-toggle-state {
        background-image: url(../../../img/icons/check-gray.svg);
    }

    &__state-layers,
    &__state-layer {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
    }

    &__state-layers {
        background-color: rgba($white, 0.7);
        transition: all 0.5s;
        opacity: 0;
        width: 0%;
        height: 0%;
        left: 50%;
        top: 50%;
        z-index: 9;

        &--loading,
        &--success {
            opacity: 1;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
        }
        &--success {
            background-color: rgba($state-ok, 0.2);
        }
    }

    &__state-layer {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__alert {
        &--error {
            color: $state-relevant;
        }
        &--warning {
            color: $state-highlighted;
        }
        &--success {
            color: $state-ok;
        }
    }

    &__element {
        &--show &__input {
            display: none;
        }

        &--edit &__value {
            display: none;
        }
    }
}
</style>